/**
 * Toast store — sistema de notificaciones global, sin dependencias externas.
 *
 * API mínima:
 *   pushToast({ kind: 'success', message: 'Proyecto borrado' })
 *   pushToast({ kind: 'critical', message: '…', duration: 0 }) // sticky
 *   dismissToast(id)
 *
 * El `Toaster` componente (montado en RootLayout) escucha este store y
 * renderiza las notificaciones top-right con animación CSS. Auto-dismiss
 * tras `duration` ms (default 4000); duration=0 fuerza sticky.
 *
 * Diseñado para reemplazar `alert()`/`confirm()` en flujos donde queremos
 * feedback discreto pero visible. Para confirmaciones destructivas con
 * type-to-confirm, usar Dialog modal en vez de toast.
 */

import { create } from 'zustand'

export type ToastKind = 'success' | 'info' | 'review' | 'critical'

export interface Toast {
  id: string
  kind: ToastKind
  message: string
  /** ms hasta auto-dismiss. 0 = sticky (el user cierra manual). Default 4000. */
  duration: number
}

export interface ToastInput {
  kind: ToastKind
  message: string
  /** ms; 0 = sticky. Si se omite, default 4000. */
  duration?: number
}

interface ToastState {
  toasts: Toast[]
  pushToast: (input: ToastInput) => string
  dismissToast: (id: string) => void
  clearAll: () => void
}

const DEFAULT_DURATION_MS = 4000

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  pushToast: (input) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const toast: Toast = {
      id,
      kind: input.kind,
      message: input.message,
      duration: input.duration ?? DEFAULT_DURATION_MS,
    }
    set((state) => ({ toasts: [...state.toasts, toast] }))
    return id
  },
  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clearAll: () => set({ toasts: [] }),
}))

/** Helper directo para uso fuera de componentes (mutations, async flows). */
export function pushToast(input: ToastInput): string {
  return useToastStore.getState().pushToast(input)
}
